import { Observable, throwError, of } from "rxjs";
import { catchError, map, shareReplay } from "rxjs/operators";
import { Injectable, EventEmitter } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Router } from "@angular/router";

import {
  HttpErrorHandler,
  HandleError,
} from "../../http-error-handler.service";
import { EndpointLocationProviderService } from "../../shared/services/endpoint-location-provider.service";
import { environment } from "../../../environments/environment";
import { ModulesEnum } from "../../shared/models/modules-enum.model";
import { Location } from "@angular/common";
import { ToastrService } from "ngx-toastr";
import { AppSettings } from "../../app.settings";
import { PermissionService } from "../../shared/services/permission.service";

@Injectable({
  providedIn: "root",
})
export class AuthenticationService {
  permissions: string[] = [];

  // Eventos de autenticação
  public statementSignedSucessfullyEvent = new EventEmitter();
  public fullAuthenticationEvent = new EventEmitter();

  // Caches
  private cachePermissionAction$: Observable<any>;
  private cacheEntitiesPermission$: Observable<any>;
  private cacheUserType$: Observable<any>;
  private cacheThumbprint$: Observable<any>;
  private CACHE_SIZE = 1;

  private handleError: HandleError;
  private env = environment;
  private userToken = "";
  private isPreferenceLoaded = false;
  private isLoadingMenu = false;

  constructor(
    private http: HttpClient,
    private endpointLocationService: EndpointLocationProviderService,
    protected httpErrorHandler: HttpErrorHandler,
    private router: Router,
    private location: Location,
    private appSettings: AppSettings,
    private toastr: ToastrService,
    private permissionService: PermissionService
  ) {
    this.handleError = httpErrorHandler.createHandleError(
      "AuthenticationService"
    );

    this.permissionService.permissions$.subscribe((newPermissions) => {
      this.permissions = newPermissions;
    });
  }

  /**
   * Autentica o usuário na API de autenticação e armazena o token como singleton para chamadas posteriores.
   */
  public authenticate(): Observable<any> {
    // Limpa os caches de identity
    this.cleanCaches();
    return this.http
      .get(
        `${this.endpointLocationService.getEndpointURI(
          this.env.API_ENDPOINT_AUTENTICACAO_AUTH
        )}/Authenticate`
      )
      .pipe(
        map((x) => {
          this.userToken = x as string;
          this.permissionService.permissions$.subscribe((newPermissions) => {
            this.permissions["USER_TOKEN"] = this.userToken;
            if (environment.production === false) {
              localStorage.setItem("USER_TOKEN", this.userToken);
            }
          });
        }),
        catchError((err) => {
          this.handleError("Autenticar", []);
          return throwError(err);
        })
      );
  }

  /**
   * Retorna o CPF do usuário autenticado.
   */
  public getIdentityCPF(): Observable<any> {
    return this.http
      .get(
        `${this.endpointLocationService.getEndpointURI(
          this.env.API_ENDPOINT_AUTENTICACAO_SESSION
        )}/GetIdentityCPF`
      )
      .pipe(catchError(this.handleError("Buscar o CPF do identity", [])));
  }

  /**
   * Retorna o Nome do usuário autenticado.
   */
  public getIdentityName(): Observable<any> {
    if (!this.permissions["identityName"]) {
      return this.http
        .get(
          `${this.endpointLocationService.getEndpointURI(
            this.env.API_ENDPOINT_AUTENTICACAO_SESSION
          )}/GetIdentityName`
        )
        .pipe(
          map((x) => {
            this.permissionService.permissions$.subscribe((newPermissions) => {
              this.permissions["identityName"] = x;
            });

            return of(this.permissions["identityName"]);
          }),
          catchError(
            this.handleError("Buscar o Nome do usuário no identity", [])
          )
        );
    } else {
      return of(this.permissions["identityName"]);
    }
  }

  /**
   * Retorna o Nome do usuário autenticado.
   */
  public getIdentityUserIncludeDate(): Observable<any> {
    if (!localStorage.getItem("identityUserIncludeDate")) {
      return this.http
        .get(
          `${this.endpointLocationService.getEndpointURI(
            this.env.API_ENDPOINT_AUTENTICACAO_SESSION
          )}/GetIdentityUserIncludeDate`
        )
        .pipe(
          map((x) => {
            localStorage.setItem("identityUserIncludeDate", JSON.stringify(x));
            return of(x);
          }),
          catchError(
            this.handleError(
              "Buscar a Data de Inclusão do usuário no identity",
              []
            )
          )
        );
    } else {
      return of(JSON.parse(localStorage.getItem("identityUserIncludeDate")));
    }
  }

  /**
   * Retorna a lista EntidadePermissao do usuário autenticado validando o cache.
   */
  public get IdentityEntitiesPermission(): Observable<any> {
    if (this.permissions["identityEntitiesPermission"]) {
      this.cacheEntitiesPermission$ = of(
        this.permissions["identityEntitiesPermission"]
      );
    } else {
      this.cacheEntitiesPermission$ = this.getIdentityEntitiesPermission().pipe(
        map((x) => {
          this.permissionService.permissions$.subscribe((newPermissions) => {
            this.permissions["identityEntitiesPermission"] = x;
          });
          return x;
        })
      );
    }

    return this.cacheEntitiesPermission$;
  }

  /**
   * Busca a lista EntidadePermissao do usuário autenticado no servidor.
   */
  public getIdentityEntitiesPermission(): Observable<any> {
    return this.http
      .get(
        `${this.endpointLocationService.getEndpointURI(
          this.env.API_ENDPOINT_AUTENTICACAO_SESSION
        )}/GetIdentityEntitiesPermission`
      )
      .pipe(
        catchError(
          this.handleError(
            "Buscar a Lista de EntidadePermissao do identity",
            []
          )
        )
      );
  }

  /**
   * Retorna a lista PermissaoAcao do usuário autenticado validando o cache.
   */
  public get IdentityPermissionAction(): Observable<any> {
    this.permissions["identityPermissionAction"];
    if (this.permissions["identityPermissionAction"]) {
      this.cachePermissionAction$ = of(
        this.permissions["identityPermissionAction"]
      );
    } else {
      this.cachePermissionAction$ = this.getIdentityPermissionAction().pipe(
        map((x) => {
          this.permissionService.permissions$.subscribe((newPermissions) => {
            this.permissions["identityPermissionAction"] = x;
          });
          return x;
        })
      );
    }

    return this.cachePermissionAction$;
  }

  /**
   * Busca a lista PermissaoAcao do usuário autenticado no servidor.
   */
  private getIdentityPermissionAction(): Observable<any> {
    return this.http
      .get(
        `${this.endpointLocationService.getEndpointURI(
          this.env.API_ENDPOINT_AUTENTICACAO_SESSION
        )}/GetIdentityPermissionAction`
      )
      .pipe(
        catchError(
          this.handleError("Buscar a Lista de PermissaoAcao do identity", [])
        )
      );
  }

  /**
   * Retorna a instância do token.
   */
  public getToken(): string {
    // return this.userToken;
    return this.permissions["USER_TOKEN"];
  }

  /**
   * Verifica se as preferências já foram carregadas.
   */
  public getIsPreferenceLoaded(): boolean {
    return this.isPreferenceLoaded;
  }

  /**
   * Seta se as preferências foram carregadas.
   */
  public setIsPreferenceLoaded(): void {
    this.isPreferenceLoaded = true;
  }

  /**
   * Reseta o token para reautenticação.
   */
  public resetToken(): void {
    // this.userToken = '';
    this.permissionService.permissions$.subscribe((newPermissions) => {
      this.permissions = [];
    });
    localStorage.removeItem("userPreference");
    localStorage.removeItem("identityUserIncludeDate");
    localStorage.removeItem("isStatementSigned");
    localStorage.removeItem("userType");
    localStorage.removeItem("thumbnail");
  }

  /**
   * Método responsável por informar se o usuário já assinou o termo de responsabilidade.
   */
  public getIsStatementSigned(): Observable<any> {
    if (!localStorage.getItem("isStatementSigned")) {
      return this.http
        .get(
          `${this.endpointLocationService.getEndpointURI(
            this.env.API_ENDPOINT_AUTENTICACAO_SESSION
          )}/GetIsStatementSigned`
        )
        .pipe(
          map((x) => {
            localStorage.setItem("isStatementSigned", JSON.stringify(x));
            return of(x);
          }),
          catchError(
            this.handleError(
              "Verificar se o termo de responsabilidade foi assinado",
              []
            )
          )
        );
    } else {
      return of(JSON.parse(localStorage.getItem("isStatementSigned")));
    }
  }

  /**
   * Armazena que o usuário assinou o termo na sessão.
   */
  public setIsStatementSigned(): void {
    this.http
      .get(
        `${this.endpointLocationService.getEndpointURI(
          this.env.API_ENDPOINT_AUTENTICACAO_SESSION
        )}/SetIsStatementSigned`
      )
      .pipe(
        catchError(
          this.handleError("Armazenar que o termo foi assinado no identity", [])
        )
      )
      .subscribe((retorno) => {
        localStorage.setItem("isStatementSigned", JSON.stringify(retorno));
        this.statementSignedSucessfullyEvent.emit();
      }); // Emite o evento indicando que o termo foi assinado
  }

  /**
   * Retorna o tipo do usuário autenticado validando o cache.
   */
  public get UserType(): Observable<any> {
    if (localStorage.getItem("userType")) {
      this.cacheUserType$ = of(JSON.parse(localStorage.getItem("userType")));
    } else {
      this.cacheUserType$ = this.getUserType().pipe(
        map((x) => {
          localStorage.setItem("userType", JSON.stringify(x));
          return x;
        })
      );
    }

    return this.cacheUserType$;
  }

  /**
   * Busca o tipo do usuário autenticado no servidor.
   */
  private getUserType(): Observable<any> {
    return this.http
      .get(
        `${this.endpointLocationService.getEndpointURI(
          this.env.API_ENDPOINT_AUTENTICACAO_SESSION
        )}/GetUserType`
      )
      .pipe(
        catchError(this.handleError("Buscar o tipo do usuário no identity", []))
      );
  }

  /**
   * Retorna o thumbprint do usuário autenticado validando o cache.
   */
  public get Thumbprint(): Observable<any> {
    if (!this.cacheThumbprint$) {
      this.cacheThumbprint$ = this.getThumbprint().pipe(
        shareReplay(this.CACHE_SIZE)
      );
    }

    return this.cacheThumbprint$;
  }

  /**
   * Busca o thumbprint do usuário autenticado no servidor.
   */
  private getThumbprint(): Observable<any> {
    return this.http
      .get(
        `${this.endpointLocationService.getEndpointURI(
          this.env.API_ENDPOINT_AUTENTICACAO_SESSION
        )}/GetThumbprint`
      )
      .pipe(
        catchError(
          this.handleError("Buscar o thumbprint do usuário no identity", [])
        )
      );
  }

  /**
   * Método responsável por retornar o thumbnail do usuário autenticado.
   */
  public getThumbnail(): Observable<any> {
    if (!localStorage.getItem("thumbnail")) {
      return this.http
        .get(
          `${this.endpointLocationService.getEndpointURI(
            this.env.API_ENDPOINT_AUTENTICACAO_SESSION
          )}/GetThumbnail`
        )
        .pipe(
          map((x) => {
            localStorage.setItem("thumbnail", JSON.stringify(x));
            return of(x);
          }),
          catchError(
            this.handleError("Buscar o thumbnail do usuário no identity", [])
          )
        );
    } else {
      return of(JSON.parse(localStorage.getItem("thumbnail")));
    }
  }

  /**
   * Método responsável por retornar o Id do usuário autenticado.
   */
  public getUserId(): Observable<any> {
    return this.http
      .get(
        `${this.endpointLocationService.getEndpointURI(
          this.env.API_ENDPOINT_AUTENTICACAO_SESSION
        )}/GetUserId`
      )
      .pipe(
        catchError(this.handleError("Buscar Id do usuário no identity", []))
      );
  }

  /**
   * Método responsável por retornar o IdSistema do usuário autenticado.
   */
  public getSystemId(): Observable<any> {
    return this.http
      .get(
        `${this.endpointLocationService.getEndpointURI(
          this.env.API_ENDPOINT_AUTENTICACAO_SESSION
        )}/GetSystemId`
      )
      .pipe(
        catchError(
          this.handleError("Buscar IdSistema do usuário no identity", [])
        )
      );
  }

  /**
   * Método responsável por emitir um evento para os componentes subscritos informando que o usuário foi autenticado em todas as instâncias de segurança do sistema.
   */
  public emitFullAuthentication() {
    this.getIsStatementSigned().subscribe((bypass) => {
      if (bypass) {
        // Solicita permissão de envio de notificações ao usuário
        // Gera o token do FCM
        // Adiciona o token no firebase
        // this.firebaseService.requestPermission(this.currentUser.CPF);
        // this.firebaseService.receiveMessage();
        // Emite o evento de fullAuthentication
        this.fullAuthenticationEvent.emit();
      }
    });
  }

  /**
   * Verifica se o usuário possuí a permissão informada no parâmetro.
   * @param idPermissao ID da permissão que o usuário precisa ter para acessar o conteúdo.
   * @param permissaoAcao Lista de PermissaoAcao do usuário autenticado.
   */
  public hasPermission(idPermissao: ModulesEnum, permissaoAcao: []): boolean {
    if (permissaoAcao) {
      return permissaoAcao.some(
        (x) => (x as any).Permissao.IdPermissao === idPermissao
      );
    } else {
      return false;
    }
  }

  /**
   * Retorna a primeira permissão que o usuário tenha na ordem dos menus para reencaminhar no login.
   */
  public getFirstPermissionToLogin(permissionAction: []): ModulesEnum {
    // Valida se tem Permissão no Dashboard
    if (this.hasPermission(ModulesEnum.Dashboard, permissionAction)) {
      return ModulesEnum.Dashboard;
    } else if (
      this.hasPermission(ModulesEnum.CapturaManual, permissionAction)
    ) {
      // Valida se tem Permissão na Captura Manual
      return ModulesEnum.CapturaManual;
    } else if (
      this.hasPermission(ModulesEnum.NotificacaoEvento, permissionAction)
    ) {
      // Valida se tem Permissão no Notificação Evento
      return ModulesEnum.NotificacaoEvento;
    } else if (
      this.hasPermission(ModulesEnum.ValidacaoExterna, permissionAction)
    ) {
      // Valida se tem Permissão na Validação Externa
      return ModulesEnum.ValidacaoExterna;
    } else if (
      this.hasPermission(ModulesEnum.DocumentoFiscal, permissionAction)
    ) {
      // Valida se tem Permissão no Documento Fiscal
      return ModulesEnum.DocumentoFiscal;
    } else if (this.hasPermission(ModulesEnum.RelatorioACI, permissionAction)) {
      // Valida se tem Permissão no Relatório ACI
      return ModulesEnum.RelatorioACI;
    } else if (
      this.hasPermission(ModulesEnum.RelatorioSolicitacao, permissionAction)
    ) {
      // Valida se tem Permissão no Relatório de Solicitação
      return ModulesEnum.RelatorioSolicitacao;
    } else if (
      this.hasPermission(ModulesEnum.RelatorioEmissao, permissionAction)
    ) {
      // Valida se tem Permissão no Relatório de Emissão
      return ModulesEnum.RelatorioEmissao;
    } else if (
      this.hasPermission(ModulesEnum.RelatorioQuantidade, permissionAction)
    ) {
      // Valida se tem Permissão no Relatório de Quantidade
      return ModulesEnum.RelatorioQuantidade;
    } else if (
      this.hasPermission(ModulesEnum.RelatorioRevogacao, permissionAction)
    ) {
      // Valida se tem Permissão no Relatório de Revogação
      return ModulesEnum.RelatorioRevogacao;
    } else if (
      this.hasPermission(ModulesEnum.RelatorioVendas, permissionAction)
    ) {
      // Valida se tem Permissão no Relatório de Vendas
      return ModulesEnum.RelatorioVendas;
    } else if (
      this.hasPermission(ModulesEnum.RelatorioSafeID, permissionAction)
    ) {
      // valida se tem Permissão no Relatório do Safe ID
      return ModulesEnum.RelatorioSafeID;
    } else if (this.hasPermission(ModulesEnum.Produto, permissionAction)) {
      // Valida se tem Permissão no Produto
      return ModulesEnum.Produto;
    } else if (
      this.hasPermission(ModulesEnum.ComercialSolicitacao, permissionAction)
    ) {
      // Valida se tem Permissão no Comercial Solicitacao
      return ModulesEnum.ComercialSolicitacao;
    } else if (
      this.hasPermission(ModulesEnum.EntidadeSafe2Pay, permissionAction)
    ) {
      // Valida se tem Permissão no EntidadeSafe2Pay
      return ModulesEnum.EntidadeSafe2Pay;
    } else if (this.hasPermission(ModulesEnum.Antifraude, permissionAction)) {
      // Valida se tem Permissão no Antifraude
      return ModulesEnum.Antifraude;
    } else if (this.hasPermission(ModulesEnum.Usuarios, permissionAction)) {
      // Valida se tem Permissão nos Usuários
      return ModulesEnum.Usuarios;
    } else if (
      this.hasPermission(
        ModulesEnum.ConferenciaPrioridadeEnvio,
        permissionAction
      )
    ) {
      // Valida se tem Permissão na Conferência Prioridade Envio
      return ModulesEnum.ConferenciaPrioridadeEnvio;
    } else if (
      this.hasPermission(
        ModulesEnum.ConferenciaProtocoloBloqueado,
        permissionAction
      )
    ) {
      // Valida se tem Permissão na Conferência Protocolo Bloqueado
      return ModulesEnum.ConferenciaProtocoloBloqueado;
    } else if (this.hasPermission(ModulesEnum.Renovacao, permissionAction)) {
      // Valida se tem Permissão na Renovação
      return ModulesEnum.Renovacao;
    } else if (
      this.hasPermission(ModulesEnum.ControleProtocolo, permissionAction)
    ) {
      // Valida se tem Permissão no Controle de Protocolos
      return ModulesEnum.ControleProtocolo;
    }
  }

  /**
   * Método responsável por atualizar o identity caso o backend solicite.
   */
  public reloadIdentity(): Observable<any> {
    // Limpa os caches de identity
    this.cleanCaches();

    return this.IdentityPermissionAction.pipe(
      map((PermissaoAcao) => {
        const location = this.location.path(false);
        const actualRoute = location.split("/")[2];
        const actualRoutePermission = (
          this.router.config[1] as any
        )._loadedConfig.routes[0].children.find(
          (route) => route.path === actualRoute
        ).data.permission;

        if (
          !this.hasPermission(
            +ModulesEnum[actualRoutePermission],
            PermissaoAcao
          )
        ) {
          return true;
        } else {
          return false;
        }
      })
    );
  }

  /**
   * Limpa os caches de autenticação.
   */
  private cleanCaches(): void {
    this.cacheEntitiesPermission$ =
      this.cachePermissionAction$ =
      this.cacheUserType$ =
      this.cacheThumbprint$ =
        null;
  }

  /**
   * Recarrega o menu da aplicação
   */
  public reloadMenu(): void {
    if (!this.isLoadingMenu) {
      this.isLoadingMenu = true;

      this.reloadIdentity().subscribe((losePermissionInActualModule) => {
        if (losePermissionInActualModule) {
          this.toastr
            .warning(
              "Suas permissões no módulo atual foram removidas e você será encaminhado para a página inicial.",
              "Atenção!",
              {
                timeOut: 5000,
                extendedTimeOut: 5000,
              }
            )
            .onHidden.subscribe(() => this.router.navigate([""]));
        }

        // Emite o evento para atualizar o menu
        this.appSettings.settings.emitters.needReloadMenu.emit(true);

        // Avisa que as permissões foram atualizadas
        this.toastr.info("Suas permissões foram atualizadas.", "Aviso!", {
          timeOut: 0,
          extendedTimeOut: 0,
        });

        // Aguarda o fim das chamadas para liberar a atualização de menu novamente
        setTimeout(() => {
          this.isLoadingMenu = false;
        }, 5000);
      });
    }
  }
}
